import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { stripeCheckout } from '../../../../lib/authentication';
import { UserFacingError, logInternalError } from '../../../../lib/errors';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import useTenancy from '../../../../hooks/useTenancy';
import * as types from '../../../../types';
import { StripeAddressValue, StripeCardDetails } from '../../../../components/StripeCheckout';
import { parseStripeAddressName } from '../../../../lib/nameParser';
import { sessionActions } from '../../../../reducers/session';

interface SubmitCheckoutParams {
  tokenId: string;
  email: string;
  address: StripeAddressValue;
  shippingCode: types.ShippingCode;
  cardDetails: StripeCardDetails;
}

export default function useStripeCheckout() {
  const { t } = useTranslation();
  const { checkoutActions } = useContext(CheckoutContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const checkoutPaths = useCheckoutPaths();
  const tenancy = useTenancy();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const submitCheckout = useCallback(
    async ({ tokenId, email, address, shippingCode, cardDetails }: SubmitCheckoutParams) => {
      setSubmitting(true);
      setError(undefined);

      const { firstName, lastName } = parseStripeAddressName(address);
      const shippingAddress: types.gqlTypes.AddressInput = {
        line1: address.address.line1,
        line2: address.address.line2 ?? '',
        city: address.address.city,
        state: address.address.state,
        zip: address.address.postal_code,
        country: address.address.country,
        phone: address.phone,
      };

      try {
        // single endpoint: creates account, saves shipping + billing
        const session = await stripeCheckout({
          confirmationToken: tokenId,
          email,
          firstName,
          lastName,
          address1: address.address.line1,
          address2: address.address.line2 ?? undefined,
          city: address.address.city,
          state: address.address.state,
          phone: address.phone ?? '',
          zip: address.address.postal_code,
          country: address.address.country,
          tenancy,
        });

        dispatch(sessionActions.createSession({ session }));

        // billing info is stored server-side by the stripe-checkout endpoint for all users,
        // so /purchases uses the stored billing info and no token is needed in the mutation.

        // dispatch checkout context from data we already have
        dispatch(checkoutActions.setCustomerName({ firstName, lastName }));
        dispatch(checkoutActions.setShippingAddress({ shippingAddress }));
        dispatch(checkoutActions.setShippingCode({ shippingCode }));

        // construct billing info from stripe confirmation token card details
        dispatch(
          checkoutActions.setBillingInfo({
            billingInfo: {
              firstName,
              lastName,
              address: {
                line1: shippingAddress.line1,
                line2: shippingAddress.line2 ?? '',
                city: shippingAddress.city,
                state: shippingAddress.state,
                zip: shippingAddress.zip,
                country: shippingAddress.country,
                phone: shippingAddress.phone,
              },
              paymentInfo: {
                __typename: 'ObfuscatedCardInfo',
                type: cardDetails.brand,
                lastFour: parseInt(cardDetails.last4, 10),
                month: cardDetails.expMonth,
                year: cardDetails.expYear,
              },
            },
          }),
        );

        history.push(checkoutPaths.Review);
      } catch (err) {
        if (err instanceof UserFacingError) {
          setError(err.message);
        } else {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
            tags: { checkoutStep: 'stripeCheckout' },
          });
          setError(t('something_went_wrong'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [t, tenancy, dispatch, checkoutActions, history, checkoutPaths.Review],
  );

  return { submitCheckout, submitting, error };
}
